import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendarResponse, Resource, Session } from '../../lib/types';
import { useUiStore } from '../../lib/uiStore';
import { useLang, localName } from '../../lib/lang';
import { useErrMsg } from '../../lib/errors';
import { api } from '../../lib/api';
import { hmsToMinutes, isoForDateMinutes, localMinutesOfDay, minutesToLabel } from '../../lib/time';
import { layout, SLOT_PX, HEADER_PX } from './layout';
import { SessionHoverCard, useHoverCard, useTagResolver } from './SessionHoverCard';

interface DragState {
  id: number;
  mode: 'move' | 'resize';
  pointerStartY: number;
  origStartMin: number;
  origDurMin: number;
  origEmpId: number;
  curStartMin: number;
  curDurMin: number;
  curEmpId: number;
  moved: boolean;
}

export function ResourceTimeline({ data, date, branchId }: { data: CalendarResponse; date: string; branchId: number }): JSX.Element {
  const { settings, resources, sessions } = data;
  const openCreate = useUiStore((s) => s.openCreate);
  const openEdit = useUiStore((s) => s.openEdit);
  const { t, lang } = useLang();
  const errMsg = useErrMsg();
  const qc = useQueryClient();
  const statusById = useMemo(() => new Map((data.statuses ?? []).map((s) => [s.id, s])), [data.statuses]);
  const statusLabel = (s: Session): string => {
    const st = statusById.get(s.status_id);
    return localName(lang, st?.name ?? s.status_name, st?.name_ar);
  };
  const rootRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const hover = useHoverCard(drag !== null);

  // Tags aren't part of the calendar payload; the list is small and shared with
  // the booking dialog under the same query key, so it's served from cache.
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: api.tags });
  const tagsOf = useTagResolver(tagsQ.data, lang);

  const dayStart = hmsToMinutes(settings.day_start_time || '09:00');
  let dayEnd = hmsToMinutes(settings.day_end_time || '21:00');
  if (dayEnd <= dayStart) dayEnd = dayStart + 12 * 60;
  const slot = Math.max(5, settings.slot_duration_minutes || 30);
  const snap = Math.max(1, settings.slot_snap_minutes || 15);
  const pxPerMin = SLOT_PX / slot;
  const total = dayEnd - dayStart;
  const bodyHeight = total * pxPerMin;

  const rows = useMemo(() => {
    const r: number[] = [];
    for (let m = dayStart; m <= dayEnd; m += slot) r.push(m);
    return r;
  }, [dayStart, dayEnd, slot]);

  const byEmp = useMemo(() => {
    const map = new Map<number, Session[]>();
    for (const s of sessions) {
      const key = s.employee_id ?? 0;
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return map;
  }, [sessions]);

  const columns: Resource[] = useMemo(() => {
    const cols = [...resources];
    if (byEmp.has(0)) cols.push({ id: 0, name: 'Unassigned', name_ar: null, color: '#94a3b8', display_order: 9999, is_bookable: false, max_daily_appointments: null, allow_overlap: false });
    return cols;
  }, [resources, byEmp]);

  const snapMin = (m: number): number => Math.round(m / snap) * snap;

  // ── drag lifecycle ──
  const startDrag = (session: Session, mode: 'move' | 'resize', e: React.PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({
      id: session.id,
      mode,
      pointerStartY: e.clientY,
      origStartMin: localMinutesOfDay(session.start_at),
      origDurMin: session.duration_minutes,
      origEmpId: session.employee_id ?? 0,
      curStartMin: localMinutesOfDay(session.start_at),
      curDurMin: session.duration_minutes,
      curEmpId: session.employee_id ?? 0,
      moved: false,
    });
  };

  useEffect(() => {
    if (!drag) return;
    // A touch that the browser decides to turn into a pan fires pointercancel,
    // never pointerup. Without this the drag state survived, and the NEXT
    // pointerup anywhere on the page committed a reschedule nobody asked for.
    const onCancel = (): void => setDrag(null);
    const onMove = (e: PointerEvent): void => {
      const deltaMin = (e.clientY - drag.pointerStartY) / pxPerMin;
      setDrag((d) => {
        if (!d) return d;
        const moved = d.moved || Math.abs(e.clientY - d.pointerStartY) > 3;
        if (d.mode === 'resize') {
          const dur = Math.max(slot, snapMin(d.origDurMin + deltaMin));
          return { ...d, curDurMin: dur, moved };
        }
        let start = snapMin(d.origStartMin + deltaMin);
        start = Math.max(dayStart, Math.min(dayEnd - d.curDurMin, start));
        // column under pointer → employee
        const colEl = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest('[data-col-empid]') as HTMLElement | null;
        const empId = colEl ? Number(colEl.dataset.colEmpid) : d.curEmpId;
        return { ...d, curStartMin: start, curEmpId: empId, moved };
      });
    };
    const onUp = (): void => {
      setDrag((d) => {
        if (!d) return null;
        if (!d.moved) {
          openEdit(d.id);
          return null;
        }
        const changed = d.curStartMin !== d.origStartMin || d.curDurMin !== d.origDurMin || d.curEmpId !== d.origEmpId;
        if (changed) {
          const patch = {
            start_at: isoForDateMinutes(date, d.curStartMin),
            duration_minutes: d.curDurMin,
            employee_id: d.curEmpId === 0 ? null : d.curEmpId,
          };
          // Paint the new position immediately; the request reconciles in the background.
          const key = ['calendar', branchId, date];
          const rollback = qc.getQueryData<CalendarResponse>(key);
          qc.setQueryData<CalendarResponse>(key, (prev) =>
            prev
              ? {
                  ...prev,
                  sessions: prev.sessions.map((s) =>
                    s.id === d.id
                      ? { ...s, ...patch, end_at: isoForDateMinutes(date, d.curStartMin + d.curDurMin) }
                      : s,
                  ),
                }
              : prev,
          );
          void api
            .updateSession(d.id, patch)
            .then(() => qc.invalidateQueries({ queryKey: ['calendar'] }))
            .catch((err) => {
              if (rollback) qc.setQueryData(key, rollback);
              alert(errMsg(err, { en: 'The appointment couldn’t be updated.', ar: 'تعذّر تحديث الموعد.' }));
              void qc.invalidateQueries({ queryKey: ['calendar'] });
            });
        }
        return null;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.id, drag?.mode]);

  const onColumnClick = (e: React.MouseEvent<HTMLDivElement>, employeeId: number): void => {
    if (drag) return;
    if ((e.target as HTMLElement).closest('[data-session]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let minutes = dayStart + snapMin(y / pxPerMin);
    minutes = Math.max(dayStart, Math.min(dayEnd - slot, minutes));
    openCreate({ employeeId: employeeId === 0 ? null : employeeId, start: isoForDateMinutes(date, minutes) });
  };

  return (
    <div ref={rootRef} dir="ltr" className="appt-scroll h-full overflow-auto bg-slate-100 dark:bg-slate-950" style={{ touchAction: drag ? 'none' : 'auto' }}>
      <div className="inline-flex min-w-full">
        {/* Time gutter */}
        <div className="sticky left-0 z-10 flex-none bg-slate-100 dark:bg-slate-950" style={{ width: 'var(--appt-gutter)' }}>
          <div className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-950" style={{ height: HEADER_PX }} />
          <div className="relative" style={{ height: bodyHeight }}>
            {rows.map((m) => (
              <div key={m} className="absolute pr-2 pt-0.5 text-right text-[11px] font-medium text-slate-400" style={{ top: (m - dayStart) * pxPerMin, right: 0, left: 0 }}>
                {minutesToLabel(m)}
              </div>
            ))}
          </div>
        </div>

        {/* Resource columns */}
        <div className="flex flex-1">
          {columns.map((res) => {
            const laid = layout(byEmp.get(res.id) ?? [], dayStart, pxPerMin);
            return (
              <div
                key={res.id}
                className="border-l border-slate-200 dark:border-slate-800"
                /* Column width narrows on a phone (see --appt-col-min) so two
                   staff are legible side by side instead of one and a sliver. */
                style={{ flex: '1 1 0', minWidth: 'var(--appt-col-min)', maxWidth: 340 }}
              >
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200 bg-white px-2 dark:border-slate-800 dark:bg-slate-900" style={{ height: HEADER_PX }}>
                  <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: res.color ?? '#cbd5e1' }} />
                  <span className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200">{res.id === 0 ? t({ en: 'Unassigned', ar: 'غير معيّن' }) : res.name}</span>
                </div>
                <div
                  data-col-empid={res.id}
                  className="relative cursor-pointer bg-white transition hover:bg-slate-50/50 dark:bg-slate-900 dark:hover:bg-slate-800/40"
                  style={{
                    height: bodyHeight,
                    backgroundImage: 'repeating-linear-gradient(to bottom, rgba(148,163,184,0.18) 0, rgba(148,163,184,0.18) 1px, transparent 1px, transparent ' + SLOT_PX + 'px)',
                  }}
                  onClick={(e) => onColumnClick(e, res.id)}
                >
                  {laid.map(({ session, top, height, lane, lanes }) => {
                    const isDragged = drag?.id === session.id;
                    const dTop = isDragged ? (drag!.curStartMin - dayStart) * pxPerMin : top;
                    const dHeight = isDragged ? Math.max(drag!.curDurMin * pxPerMin - 3, 20) : height;
                    // hide the original block in its source column while dragging to another column
                    if (isDragged && drag!.curEmpId !== res.id) return null;
                    return (
                      <SessionBlock
                        key={session.id}
                        session={session}
                        top={dTop}
                        height={dHeight}
                        lane={isDragged ? 0 : lane}
                        lanes={isDragged ? 1 : lanes}
                        dragging={isDragged}
                        statusLabel={statusLabel(session)}
                        onStartMove={(e) => startDrag(session, 'move', e)}
                        onStartResize={(e) => startDrag(session, 'resize', e)}
                        onOpen={() => openEdit(session.id)}
                        onHoverStart={(el) =>
                          hover.open(el, () => ({
                            session,
                            statusLabel: statusLabel(session),
                            statusColor: session.color ?? session.status_color ?? '#3B82F6',
                            employeeName: res.id === 0 ? t({ en: 'Unassigned', ar: 'غير معيّن' }) : res.name,
                            tags: tagsOf(session),
                          }))
                        }
                        onHoverEnd={hover.close}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {hover.target && <SessionHoverCard target={hover.target} />}
    </div>
  );
}

/**
 * One appointment on the timeline.
 *
 * Mouse and touch get deliberately different affordances:
 *
 * - **Mouse** — press anywhere and drag to move, as before. A press without
 *   movement opens the editor.
 * - **Touch** — the body of the block does NOT start a drag. It used to, and
 *   the result was that panning the calendar with a finger that happened to
 *   land on an appointment moved that appointment: `pointerdown` armed the
 *   drag, the browser panned anyway (the scroll container's `touch-action` was
 *   still `auto` at that point), and the release committed a reschedule. A tap
 *   now opens the editor; moving is done from the explicit grip, and resizing
 *   from the bottom edge — both of which set `touch-action: none` so the
 *   browser never competes for the gesture.
 */
function SessionBlock({
  session, top, height, lane, lanes, dragging, statusLabel, onStartMove, onStartResize, onOpen, onHoverStart, onHoverEnd,
}: {
  session: Session; top: number; height: number; lane: number; lanes: number; dragging: boolean; statusLabel: string;
  onStartMove: (e: React.PointerEvent) => void; onStartResize: (e: React.PointerEvent) => void;
  onOpen: () => void;
  onHoverStart: (el: HTMLElement) => void; onHoverEnd: () => void;
}): JSX.Element {
  const { t } = useLang();
  const color = session.color ?? session.status_color ?? '#3B82F6';
  const widthPct = 100 / lanes;
  const compact = height < 44;
  const who = session.customer_name ?? t({ en: 'Walk-in', ar: 'زائر' });
  const svcLabel = t({ en: 'svc', ar: 'خدمة' });

  // Where a touch went down, so a tap can be told from a swipe that the
  // browser turned into a pan.
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  return (
    <div
      data-session
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse') onStartMove(e);
        else touchStart.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUp={(e) => {
        if (e.pointerType === 'mouse') return;
        const s = touchStart.current;
        touchStart.current = null;
        if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) < 10) onOpen();
      }}
      onPointerCancel={() => { touchStart.current = null; }}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') onHoverStart(e.currentTarget); }}
      onPointerLeave={onHoverEnd}
      className={`group absolute select-none overflow-hidden rounded-lg px-2 py-1 text-start shadow-sm ring-1 ring-black/5 transition-shadow hover:z-10 hover:shadow-md ${dragging ? 'z-30 cursor-grabbing opacity-90 shadow-lg' : 'cursor-grab'}`}
      style={{
        top: top + 1, height,
        left: `calc(${lane * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)`,
        background: `${color}1a`, borderInlineStart: `3px solid ${color}`,
      }}
    >
      <div className="truncate pe-5 text-xs font-semibold text-slate-800 dark:text-slate-100">{who}</div>
      {!compact && (
        <>
          <div className="truncate text-[11px] text-slate-500 dark:text-slate-400">
            {minutesToLabel(localMinutesOfDay(session.start_at))}
            {session.services.length > 0 && ` · ${session.services.length} ${svcLabel}`}
          </div>
          <div className="mt-0.5 inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
            <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color }}>{statusLabel}</span>
          </div>
        </>
      )}

      {/* Move grip — the only way to drag on touch, and a redundant one on
          desktop where the whole block already drags. */}
      <div
        onPointerDown={onStartMove}
        className="absolute end-0 top-0 grid h-6 w-6 cursor-grab place-items-center text-slate-400 opacity-60 transition-opacity hover:opacity-100"
        style={{ touchAction: 'none' }}
        aria-label={t({ en: 'Drag to move', ar: 'اسحب للنقل' })}
        role="button"
      >
        <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor" aria-hidden>
          <circle cx="2.5" cy="2" r="1.2" /><circle cx="7.5" cy="2" r="1.2" />
          <circle cx="2.5" cy="6" r="1.2" /><circle cx="7.5" cy="6" r="1.2" />
          <circle cx="2.5" cy="10" r="1.2" /><circle cx="7.5" cy="10" r="1.2" />
        </svg>
      </div>

      {/* Resize handle — 12px tall so a fingertip can find it. */}
      <div
        onPointerDown={onStartResize}
        className="absolute inset-x-0 bottom-0 h-3 cursor-ns-resize"
        style={{ touchAction: 'none' }}
        aria-label={t({ en: 'Drag to change duration', ar: 'اسحب لتغيير المدة' })}
        role="button"
      />
    </div>
  );
}
